/**
 * **Sasaran promo** — baris mana yang boleh kena diskon, untuk promo ber-`scope: line`.
 *
 * ### ⛔⛔ Kenapa ini penting, dan kenapa kosongnya berbahaya
 * Mesin promo membacanya begini (`pos-core/promo.ts`): **tanpa satu baris `promo_target` pun,
 * promo berlaku ke SEMUA baris** yang boleh didiskon. Aturan itu benar — tapi selama tak ada
 * layarnya, pilihan `scope: line` di layar promo **tak pernah berbeda dari `order`**, dan "diskon
 * 20% khusus minuman" mustahil dibuat siapa pun.
 *
 * ⇒ Yang paling mudah salah paham: **menambah sasaran itu MENYEMPITKAN, bukan memperluas.** Begitu
 *   ada satu baris, promo berhenti berlaku ke yang lain. Karena itu keadaan kosong di sini tak
 *   ditulis "belum ada data" melainkan menyebut akibatnya.
 *
 * ### ⚠️ Tiga tingkat, dan yang paling luas menang
 * Departemen · kategori · produk. Satu baris di tingkat departemen sudah mencakup seluruh kategori
 * & produk di bawahnya — mendaftarkan keduanya bukan salah, cuma mubazir.
 */
import { Alert, Button, Card, Select, Space, Table, Tag, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Category, Department, Product } from '../api/client';
import { canWrite } from '../api/session';

interface TargetRow {
  readonly id: string;
  readonly department_id: string | null;
  readonly category_id: string | null;
  readonly product_id: string | null;
}

export function PromoTargets({ promoId }: { readonly promoId: string }) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [rows, setRows] = useState<readonly TargetRow[]>([]);
  const [dept, setDept] = useState<readonly Department[]>([]);
  const [kat, setKat] = useState<readonly Category[]>([]);
  const [produk, setProduk] = useState<readonly Product[]>([]);
  const [pilih, setPilih] = useState<string | undefined>(undefined);

  const muat = useCallback(async () => {
    setRows(await api.listMaster<TargetRow>('promo-targets', { promo_id: promoId }));
  }, [promoId]);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);
  useEffect(() => {
    void api.departments().then(setDept).catch(() => undefined);
    void api.listMaster<Category>('categories').then(setKat).catch(() => undefined);
    void api.listMaster<Product>('products').then(setProduk).catch(() => undefined);
  }, []);

  const jalankan = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await muat();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * Satu daftar berisi tiga tingkat, dibedakan awalan nilainya.
   *
   * ⚠️ Sengaja SATU kotak, bukan tiga: yang dijawab orang adalah *"promo ini untuk apa?"* — dan
   * memaksanya memilih tingkat lebih dulu berarti ia harus tahu jawabannya sebelum bertanya.
   */
  const pilihan = [
    {
      label: t('promoTarget.department'),
      options: dept.map((d) => ({ value: `d:${d.id}`, label: `${d.code} · ${d.name}` })),
    },
    {
      label: t('promoTarget.category'),
      options: kat.map((k) => ({ value: `k:${k.id}`, label: `${k.code} · ${k.name}` })),
    },
    {
      label: t('promoTarget.product'),
      options: produk.map((p) => ({ value: `p:${p.id}`, label: `${p.code} · ${p.name}` })),
    },
  ];

  const nama = (r: TargetRow): { jenis: string; teks: string } => {
    if (r.department_id !== null) {
      return { jenis: t('promoTarget.department'), teks: dept.find((d) => d.id === r.department_id)?.name ?? '—' };
    }
    if (r.category_id !== null) {
      return { jenis: t('promoTarget.category'), teks: kat.find((k) => k.id === r.category_id)?.name ?? '—' };
    }
    return { jenis: t('promoTarget.product'), teks: produk.find((p) => p.id === r.product_id)?.name ?? '—' };
  };

  return (
    <Card size="small" data-testid="promo-sasaran">
      {rows.length === 0 ? (
        /*
         * ⛔ Bukan "belum ada data". Kosong di sini punya ARTI, dan artinya kebalikan dari yang
         * orang duga: promo berlaku ke SELURUH baris.
         */
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
               title={t('promoTarget.emptyMeans')} data-testid="promo-sasaran-kosong" />
      ) : (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} title={t('promoTarget.narrows')} />
      )}

      <Table<TargetRow>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={[...rows]}
        locale={{ emptyText: t('promoTarget.none') }}
        columns={[
          {
            title: t('promoTarget.level'),
            width: 130,
            render: (_: unknown, r: TargetRow) => <Tag>{nama(r).jenis}</Tag>,
          },
          { title: t('promoTarget.what'), render: (_: unknown, r: TargetRow) => nama(r).teks },
          {
            title: '',
            width: 90,
            render: (_: unknown, r: TargetRow) => (
              <Button size="small" danger disabled={!boleh}
                      data-testid={`promo-sasaran-hapus-${r.id}`}
                      onClick={() => void jalankan(() => api.deleteMaster('promo-targets', r.id))}>
                {t('floor.delete')}
              </Button>
            ),
          },
        ]}
      />

      {boleh && (
        <Space style={{ marginTop: 12 }}>
          <Select
            showSearch
            style={{ width: 320 }}
            placeholder={t('promoTarget.pick')}
            optionFilterProp="label"
            value={pilih}
            onChange={setPilih}
            options={pilihan}
            data-testid="promo-sasaran-pilih"
          />
          <Button
            type="primary"
            disabled={pilih === undefined}
            data-testid="promo-sasaran-tambah"
            onClick={() => {
              if (pilih === undefined) return;
              const [jenis, id] = [pilih.slice(0, 1), pilih.slice(2)];
              void jalankan(async () => {
                await api.createMaster('promo-targets', {
                  promoId,
                  /* ⛔ Tepat SATU dari tiga terisi — CHECK di DDL menuntutnya. */
                  ...(jenis === 'd' ? { departmentId: id } : {}),
                  ...(jenis === 'k' ? { categoryId: id } : {}),
                  ...(jenis === 'p' ? { productId: id } : {}),
                });
                setPilih(undefined);
              });
            }}
          >
            {t('promoTarget.add')}
          </Button>
        </Space>
      )}
    </Card>
  );
}
